import { AUTH_ROUTES } from "./authHelpers.ts";
import type { StorageLike } from "./federatedLinkRetry.ts";

/**
 * Where a signed-out visitor was headed, so signing in takes them there.
 *
 * The sign-in screens live at their own Urls, so a link into the app -- a
 * consent screen an app such as Claude sent someone to, a contact from an
 * email -- was replaced by `/login`, and signing in landed on the dashboard.
 * The sign-in screen remembers the link instead, and AuthContext puts it back
 * in the address bar the moment someone is signed in, before anything reads
 * it. Session storage, because a sign-in through Google or single sign-on
 * leaves the app and comes back to its root in the same tab.
 */
const RETURN_PATH = "spectacle.returnPath";

/**
 * How long a remembered link is followed: a consent request's lifetime
 * (OauthAuthorizationRequest::LIFETIME). A sign-in later than that is not the
 * one the link was for, and may not be the same person's.
 */
const MAX_AGE_MS = 15 * 60 * 1000;

const AUTH_PATHS: readonly string[] = Object.values(AUTH_ROUTES);

/** Remember the location, unless there is nothing to come back to. */
export function rememberReturnPath(
    location: { pathname: string; search: string; hash: string },
    storage: StorageLike | undefined = globalThis.sessionStorage,
    now: number = Date.now(),
): void {
    if (location.pathname === "/" || AUTH_PATHS.includes(location.pathname)) {
        return;
    }
    try {
        storage?.setItem(
            RETURN_PATH,
            JSON.stringify({
                path: `${location.pathname}${location.search}${location.hash}`,
                at: now,
            }),
        );
    } catch {
        // Storage unavailable: sign-in lands on the dashboard, as before.
    }
}

/**
 * The remembered path, handed out once while it is fresh. Only a path on
 * this origin comes back, judged the way the browser will read it: `//host`
 * and `/\host` both name another origin, so they are dropped, never followed.
 */
export function takeReturnPath(
    storage: StorageLike | undefined = globalThis.sessionStorage,
    now: number = Date.now(),
    origin: string = globalThis.location?.origin ?? "",
): string | null {
    try {
        const stored = storage?.getItem(RETURN_PATH) ?? null;
        storage?.removeItem(RETURN_PATH);
        const { path, at } = JSON.parse(stored ?? "null") ?? {};
        if (
            typeof path !== "string" ||
            typeof at !== "number" ||
            now - at > MAX_AGE_MS
        ) {
            return null;
        }
        const url = new URL(path, origin);
        return url.origin === origin
            ? `${url.pathname}${url.search}${url.hash}`
            : null;
    } catch {
        return null;
    }
}

/**
 * Put the remembered path in the address bar, if there is one. Called as the
 * app turns signed in: the routers, and the checks before them, read their
 * first location from there. It never throws, so it cannot keep anyone from
 * being signed in.
 */
export function restoreReturnPath(): void {
    const path = takeReturnPath();
    if (!path) {
        return;
    }
    try {
        window.history.replaceState(window.history.state, "", path);
    } catch {
        // Not followed: sign-in lands where it would have anyway.
    }
}
